import { saveTextContent } from '@paragon/client-tools';
import { sealingService } from '@paragon/data-connections';
import { enqueueError, enqueueWarning } from '@paragon/notification-tools';
import { closeCurrentPopup } from '@paragon/popup-state';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';

import { getSelectedAnnotationsForCurrentPage } from '../selectors/annotations';
import { getSelectedPage } from '../selectors/documents';
import { templateApplied } from '../store/annotations';
import { loadAnnotationsForCurrentPage, loadAnnotationsForPage, savePageAnnotations } from './annotations';
import { showFilenamePrompt } from './popup';
import { AsyncThunkActionVoid, ThunkActionVoid } from './thunkActions';

interface TemplateModifierParameters {
  readonly jobId: number;
  readonly activePageId: number;
  readonly activePdfId: number;
  readonly selectedTemplateId: number;
}

function createDocumentAnnotationModifier(
  templateModifierCallback: (parameters: TemplateModifierParameters) => Promise<void>,
) {
  return (): AsyncThunkActionVoid => async (dispatch, getState) => {
    const state = getState();
    const selectedTemplateId = state.annotationTemplates.selectedTemplate;
    const { jobId } = state.job;
    const activePageId = getSelectedPage(state)!.id;
    const activePdfId = state.documents.activeDocumentId!;

    if (selectedTemplateId == null) {
      return;
    }

    const spinnerId = dispatch(addSpinnerMessage('Applying template...'));

    try {
      await dispatch(savePageAnnotations());

      await templateModifierCallback({ jobId, activePageId, activePdfId, selectedTemplateId });

      dispatch(templateApplied());
      await dispatch(loadAnnotationsForCurrentPage());
    } catch (err) {
      // TODO: check what message Matt wants here
      dispatch(enqueueError('Error applying template. Please contact Paragon Customer Support'));
      console.error(err);
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export const applySelectedTemplateToActivePageWithoutTranslation = createDocumentAnnotationModifier(
  ({ activePageId, selectedTemplateId }) =>
    sealingService.annotationTemplates.applyAnnotationTemplateToPage(activePageId, selectedTemplateId, false),
);

export const applySelectedTemplateToActivePage = createDocumentAnnotationModifier(
  ({ activePageId, selectedTemplateId }) =>
    sealingService.annotationTemplates.applyAnnotationTemplateToPage(activePageId, selectedTemplateId),
);

export const applySelectedTemplateToAllPagesInPdf = createDocumentAnnotationModifier(
  ({ jobId, activePdfId, selectedTemplateId }) =>
    sealingService.annotationTemplates.applyAnnotationTemplateToAllPagesInPdf(jobId, activePdfId, selectedTemplateId),
);

export const applySelectedTemplateToFirstPageOfEveryComponentPdf = createDocumentAnnotationModifier(
  ({ jobId, selectedTemplateId }) =>
    sealingService.annotationTemplates.applyAnnotationTemplateToFirstPageOfEveryComponentPdf(jobId, selectedTemplateId),
);

export function resetAnnotationsForActivePage(): AsyncThunkActionVoid {
  return (dispatch, getState) => {
    const activePage = getSelectedPage(getState())!;
    return sealingService.pdfPages.reset(activePage.id).then(() => dispatch(loadAnnotationsForCurrentPage()));
  };
}

export function downloadSelectedAnnotationsOnCurrentPage(): ThunkActionVoid {
  return (dispatch, getState) => {
    const state = getState();
    const annotationsToDownload = getSelectedAnnotationsForCurrentPage(state);

    if (annotationsToDownload.length === 0) {
      dispatch(enqueueWarning('No annotations are selected'));
      return;
    }

    dispatch(
      showFilenamePrompt({
        format: '.json',
        action: 'Download Selected',
        async submitClickAction(filename) {
          let savedFilename = filename;

          if (filename.substring(filename.length - 5) !== '.json') {
            savedFilename += '.json';
          }

          dispatch(closeCurrentPopup());

          const annotationSet = await sealingService.pdfPages.createAnnotationSet(annotationsToDownload);
          saveTextContent(savedFilename, JSON.stringify(annotationSet));
        },
      }),
    );
  };
}

export function resetAnnotationsForActiveDocument(): AsyncThunkActionVoid {
  return (dispatch, getState) => {
    const state = getState();
    const { activeDocumentId } = state.documents;
    return sealingService.pdfs.reset(activeDocumentId!).then(() => {
      for (const page of state.documents.pdfData[activeDocumentId!].pdfPages) {
        dispatch(loadAnnotationsForPage(page.id));
      }
    });
  };
}
